import type { ServiceLabel } from '@rythmo/api-client';

/**
 * The public values the console runs on, read at start from `/config.json`, which the image writes
 * from its environment (`apps/admin/Dockerfile`). Nothing here is a secret: an OIDC client id and
 * the hosts are public by nature. One image therefore serves staging and production.
 */
export interface ConsoleConfig {
  /** Zitadel, e.g. `https://auth.staging.rythmo.io`. */
  issuer: string;
  /** The admin console's Zitadel application (M1-04). */
  clientId: string;
  /** The Rythmo API project: every access token is audienced to it. */
  projectId: string;
  /** `api.staging.rythmo.io`; each service is `<label>.<apiDomain>` (ADR-0058). */
  apiDomain: string;
  /** Local development only: a service's origin when it is not on `<label>.<apiDomain>`. */
  serviceOrigins?: Partial<Record<ServiceLabel, string>>;
}

const REQUIRED = ['issuer', 'clientId', 'projectId', 'apiDomain'] as const;

export async function loadConfig(
  fetchConfig: () => Promise<Response> = () => fetch('/config.json', { cache: 'no-store' }),
): Promise<ConsoleConfig> {
  const response = await fetchConfig();
  if (!response.ok) {
    throw new Error(
      `/config.json answered ${response.status}; the console cannot start without it`,
    );
  }

  const raw = (await response.json()) as Record<string, unknown>;
  for (const key of REQUIRED) {
    if (typeof raw[key] !== 'string' || raw[key] === '') {
      throw new Error(`/config.json has no ${key}`);
    }
  }

  const config: ConsoleConfig = {
    issuer: raw['issuer'] as string,
    clientId: raw['clientId'] as string,
    projectId: raw['projectId'] as string,
    apiDomain: raw['apiDomain'] as string,
  };
  if (typeof raw['serviceOrigins'] === 'object' && raw['serviceOrigins'] !== null) {
    config.serviceOrigins = raw['serviceOrigins'] as ConsoleConfig['serviceOrigins'];
  }
  return config;
}
